import axios from 'axios';
import { get, lowerCase, startCase } from 'lodash';
import { stats } from '@fiverr-private/obs';
import { STATS_METRIC_PREFIX } from '../../../ContactSellerModal/constants';
import { HttpMethod, HttpStatusCode, OperationStatus } from '../../../types';
import { logWarning } from '../../logging';

class BaseAPI {
    constructor(context) {
        this.context = context;
    }

    /**
     * Fire a GET request
     *
     * @param {Object} config
     * @param {string} config.url The request url
     * @param {Object} [config.payload] The request payload
     * @param {Object} [config.headers] The request headers
     * @param {string} config.apiName The request api name
     */
    async get({ url, payload = {}, headers = {}, apiName }) {
        return this.fireRequest({
            method: HttpMethod.GET,
            url,
            payload,
            headers,
            apiName
        });
    }

    /**
     * Fire a POST request
     *
     * @param {Object} config
     * @param {string} config.url The request url
     * @param {Object} [config.payload] The request payload
     * @param {Object} [config.headers] The request headers
     * @param {string} config.apiName The request api name
     */
    async post({ url, payload = {}, headers = {}, apiName }) {
        return this.fireRequest({
            method: HttpMethod.POST,
            url,
            payload,
            headers,
            apiName
        });
    }

    /**
     * Fire a PUT request
     *
     * @param {Object} config
     * @param {string} config.url The request url
     * @param {Object} [config.payload] The request payload
     * @param {Object} [config.headers] The request headers
     * @param {string} config.apiName The request api name
     */
    async put({ url, payload = {}, headers = {}, apiName }) {
        return this.fireRequest({
            method: HttpMethod.PUT,
            url,
            payload,
            headers,
            apiName
        });
    }

    /**
     * Fire a DELETE request
     *
     * @param {Object} config
     * @param {string} config.url The request url
     * @param {Object} [config.payload] The request payload
     * @param {Object} [config.headers] The request headers
     * @param {string} config.apiName The request api name
     */
    async delete({ url, payload = {}, headers = {}, apiName }) {
        return this.fireRequest({
            method: HttpMethod.DELETE,
            url,
            payload,
            headers,
            apiName
        });
    }

    /**
     * Fire an http request and report relevant metrics.
     *
     * @param {Object} config
     * @param {string} url The request url
     * @param {Object} [payload] The request payload
     * @param {Object} [config.headers] The request headers
     * @param {string} config.apiName The request api name
     */
    async fireRequest({ method, url, payload = {}, headers = {}, apiName }) {
        const startTime = Date.now();

        try {
            const response = await axios({
                method,
                url,
                headers,
                data: payload
            });

            this.reportRequestStatus(`${apiName}.${OperationStatus.SUCCESS}`);
            this.reportRequestTime(OperationStatus.SUCCESS, apiName, startTime);

            return response;
        } catch (error) {
            const { status = HttpStatusCode.UNKNOWN } = get(error, 'response', {});

            this.reportRequestStatus(`${apiName}.${OperationStatus.FAILED}.${status}`);
            this.reportRequestTime(OperationStatus.FAILED, apiName, startTime);
            this.logRequest({ url, payload, error, status, apiName });

            throw new Error(error);
        }
    }

    /**
     * Report a request status metric.
     *
     * @param {string} suffix The metric suffix
     */
    reportRequestStatus(suffix) {
        stats.count(STATS_METRIC_PREFIX, `api.${this.context}.${suffix}`);
    }

    /**
     * Report a request time measurement metric.
     *
     * @param {string} operationStatus The request operation status
     * @param {string} apiName The request api name
     * @param {number} startTime The request starting time
     */
    reportRequestTime(operationStatus, apiName, startTime) {
        const timePassed = Date.now() - startTime;

        stats.time(STATS_METRIC_PREFIX, `api.${this.context}.${apiName}.${operationStatus}`, timePassed);
    }

    /**
     * Log a contextualize error for the request
     *
     * @param {Object} config
     * @param {string} url The request url
     * @param {Object} [payload] The request payload
     * @param {Object} [error] The request payload
     * @param {number} status The response status code
     * @param {string} config.apiName The request api name
     */
    logRequest({ url, payload, error, status, apiName }) {
        const reportedContext = lowerCase(startCase(this.context));

        const description = `Failed while calling ${reportedContext} - ${apiName}`;

        const request = {
            context: reportedContext,
            apiName,
            url,
            payload
        };

        const response = {
            status
        };

        logWarning({
            error,
            request,
            response,
            description
        });
    }
}

export default BaseAPI;
